import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal } from "@/config/antd";
import { CloseOutlined } from "@/config/antd";
import Button from "@/components/common/Button";
import Input from "@/components/common/inputs/Input";
import WebIcon from "@/components/common/Web_Icons";
import CountryFlag from "@/components/common/Country_Flag";
import BulkInviteModal from "@/pages/user_management/components/Bulk_Invite_Modal";
import Success from "@/components/common/alerts/Success";
import { useDispatch } from "react-redux";
import {
  createUserAsync,
  updateUserAsync,
} from "@/store/redux/slices/user_admin/user_admin";
import { searchUserByFieldAsync } from "@/store/redux/slices/user/user_slice";
import { useLoader } from "@/hooks/LoaderProvider";
import { resolveRoleName } from "@/utils/organization_id";

const InviteUserFormModal = ({
  isVisible,
  onClose,
  onUserCreated,
  userData,
  isEditMode = false,
  userType = "sdc",
}) => {
  const [showBulkInvite, setShowBulkInvite] = useState(false);
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useLoader();

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    role: "",
    party: "",
  });

  useEffect(() => {
    if (isEditMode && userData) {
      setFormData({
        id: userData.id || userData._id || userData.key,
        fullName: userData.name || userData.fullname || "",
        email: userData.email || "",
        phone: userData.phone || "",
        role:
          resolveRoleName(userData.role) ||
          resolveRoleName(userData.roles) ||
          "",
        party: userData.party || "",
      });
    } else {
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        role: "",
        party: "",
      });
    }
  }, [isEditMode, userData, isVisible]);

  const handleInputChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const roleOptions = [
    { value: "admin", label: "Admin" },
    { value: "manager", label: "Manager" },
    { value: "user", label: "User" },
  ];
  const partyOptions = [
    { value: "pdp", label: "PDP" },
    { value: "apc", label: "APC" },
    { value: "lp", label: "LP" },
  ];

  const handleBulkInvite = (e) => {
    e.preventDefault();
    setShowBulkInvite(true);
  };

  const handleInvite = async () => {
    try {
      onClose(); // Close invite form
      if (onUserCreated) {
        onUserCreated(); // Close parent modal
      }

      showLoader();

      if (isEditMode && formData.id) {
        await dispatch(updateUserAsync(formData)).unwrap();
      } else {
        // For ALL invites, first check if user exists by email
        let inviteScenario = "invite/guest"; // Default to full registration

        try {
          const result = await dispatch(
            searchUserByFieldAsync({
              key: "email",
              value: formData.email,
            })
          ).unwrap();

          // If we get here, user exists
          const userExists = result && result.data && result.data.length > 0;

          // If user exists, use invite/member (password only)
          // Otherwise use invite/guest (full registration)
          inviteScenario = userExists ? "invite/member" : "invite/guest";
        } catch (error) {
          // If error is 404, user doesn't exist - use invite/guest
          if (error.statusCode === 404) {
            inviteScenario = "invite/guest";
          }
        }

        await dispatch(
          createUserAsync({
            ...formData,
            userType,
            inviteScenario,
          })
        ).unwrap();
      }

      setIsSuccessVisible(true);

      setFormData({
        fullName: "",
        email: "",
        phone: "",
        role: "",
        party: "",
      });
    } catch (error) {
      console.error("Failed to save user:", error);
    } finally {
      hideLoader();
    }
  };

  const handleSuccessClose = () => {
    setIsSuccessVisible(false);
    setFormData({
      fullName: "",
      email: "",
      phone: "",
      role: "",
      party: "",
    });

    if (onUserCreated) {
      onUserCreated(); // Notify parent component
    }
  };

  return (
    <>
      <Modal
        open={isVisible}
        onCancel={onClose}
        footer={null}
        centered
        width={600}
        closeIcon={null}
        styles={{
          body: { padding: 0 },
          content: {
            padding: 0,
            borderRadius: "16px",
            overflow: "hidden",
          },
          mask: { backgroundColor: "rgba(0, 0, 0, 0.75)" },
        }}>
        <div className="flex flex-col bg-white">
          {/* Header */}
          <div className="p-4 px-10 flex justify-between items-center">
            <h2 className="heading-regular text-primary">
              {isEditMode ? "Edit Member" : "Invite a New Member"}
            </h2>
            <CloseOutlined
              onClick={onClose}
              className="cursor-pointer"
              style={{
                color: "var(--veegil-color-primary)",
                backgroundColor: "var(--veegil-color-lighter-blue)",
                borderRadius: "50%",
                padding: "4px",
              }}
            />
          </div>

          {/* Content Area */}
          <div className="flex flex-col px-10 pt-2 pb-10 space-y-6">
            <Input
              label="FULL NAME"
              placeholder="Full Name"
              icon={<WebIcon icon="Profile" size="small" />}
              value={formData.fullName}
              onChange={handleInputChange("fullName")}
            />

            <Input
              label="EMAIL ADDRESS"
              placeholder="email@domain.com"
              icon={<WebIcon icon="Message" size="small" />}
              type="email"
              value={formData.email}
              onChange={handleInputChange("email")}
            />

            <Input
              label="PHONE NUMBER"
              placeholder="Phone Number"
              icon={<CountryFlag />}
              type="tel"
              value={formData.phone}
              onChange={handleInputChange("phone")}
            />

            <Input
              label="USER ROLE"
              placeholder="Select"
              type="select"
              options={roleOptions}
              value={formData.role}
              onChange={handleInputChange("role")}
            />
            <Input
              label="POLITICAL PARTY"
              placeholder="Select"
              type="select"
              options={partyOptions}
              value={formData.party}
              onChange={handleInputChange("party")}
            />
          </div>

          {/* Footer */}
          <div className="w-full bg-secondary p-4">
            <Button
              text={isEditMode ? "SAVE CHANGES" : "INVITE MEMBER"}
              color="primary"
              className="w-full"
              customBgColor="primary"
              customTextColor="white"
              onClick={handleInvite}
            />

            {!isEditMode && (
              <div className="text-center mt-4">
                <p className="text-small text-lighterGrey">
                  Need to add several coworkers at once?
                </p>
                <a
                  href="#"
                  className="text-primary font-bold text-small"
                  onClick={handleBulkInvite}>
                  Invite them with a link instead
                </a>
              </div>
            )}
          </div>
        </div>
      </Modal>

      <BulkInviteModal
        isVisible={showBulkInvite}
        onClose={() => setShowBulkInvite(false)}
      />

      <Success
        isVisible={isSuccessVisible}
        onClose={handleSuccessClose}
        header={
          isEditMode ? "User Updated Successfully" : "User Invited Successfully"
        }
        paragraph={
          isEditMode
            ? `${formData.fullName}'s information has been successfully updated.`
            : `${formData.fullName} has been successfully invited to join the platform.`
        }
        buttonText="Done"
      />
    </>
  );
};

InviteUserFormModal.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
  onUserCreated: PropTypes.func,
  userData: PropTypes.object,
  isEditMode: PropTypes.bool,
  userType: PropTypes.string,
};

export default InviteUserFormModal;
